import { type PropsWithChildren, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/AuthProvider";
import { purgeSensitiveBrowserCaches } from "@/lib/pwaCache";

export function AccountCacheBoundary({ children }: PropsWithChildren) {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (loading) return;

    const currentUserId = user?.id ?? null;

    // One-time cleanup for clients that installed an older service worker.
    // That worker cached Supabase GET responses without varying on auth
    // headers, so keeping the cache can reintroduce stale account data even
    // after the fixed worker takes control.
    if (!initialized.current) {
      void purgeSensitiveBrowserCaches();
    }

    if (initialized.current && previousUserId.current !== currentUserId) {
      queryClient.clear();
      void purgeSensitiveBrowserCaches();
    }

    previousUserId.current = currentUserId;
    initialized.current = true;
  }, [loading, queryClient, user?.id]);

  return children;
}
